/**
 * Chart export utilities: serialize a live chart <svg> to a PNG.
 *
 * The charts style themselves with CSS variables and `currentColor`, which only resolve in
 * the live DOM — a naively serialized SVG renders black-on-nothing. So before serializing we
 * clone the SVG and bake the COMPUTED values of the paint/typography properties into inline
 * attributes on every node. Used by the chart copy button and the PDF export.
 */

const INLINED_PROPS = [
	'fill', 'fill-opacity', 'stroke', 'stroke-opacity', 'stroke-width',
	'font-family', 'font-size', 'font-weight', 'letter-spacing', 'opacity'
] as const;

/** Clone `svg` with computed styles baked in as attributes, sized for standalone rendering. */
export function inlineSvgStyles(svg: SVGSVGElement): SVGSVGElement {
	const clone = svg.cloneNode(true) as SVGSVGElement;

	const src = [svg, ...Array.from(svg.querySelectorAll<SVGElement>('*'))];
	const dst = [clone, ...Array.from(clone.querySelectorAll<SVGElement>('*'))];
	for (let i = 0; i < src.length; i++) {
		const computed = getComputedStyle(src[i]);
		for (const prop of INLINED_PROPS) {
			const value = computed.getPropertyValue(prop);
			if (value) { dst[i].setAttribute(prop, value); }
		}
	}

	const rect = svg.getBoundingClientRect();
	clone.setAttribute('width',  String(Math.max(1, Math.round(rect.width))));
	clone.setAttribute('height', String(Math.max(1, Math.round(rect.height))));
	clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
	return clone;
}

/** Renders the chart SVG to a PNG data URL (2x for crispness), on an opaque background. */
export function svgToPngDataUrl(svg: SVGSVGElement, background = '#ffffff', scale = 2): Promise<string> {
	return new Promise((resolve, reject) => {
		const inlined = inlineSvgStyles(svg);
		const width   = Number(inlined.getAttribute('width'));
		const height  = Number(inlined.getAttribute('height'));
		const url     = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(inlined)], { type: 'image/svg+xml' }));

		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			const canvas  = document.createElement('canvas');
			canvas.width  = width * scale;
			canvas.height = height * scale;
			const ctx = canvas.getContext('2d');
			if (!ctx) { reject(new Error('no 2d context')); return; }
			ctx.fillStyle = background;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			resolve(canvas.toDataURL('image/png'));
		};
		img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('svg rasterization failed')); };
		img.src = url;
	});
}

/** PNG data URL → Blob (for ClipboardItem). */
export function dataUrlToBlob(dataUrl: string): Blob {
	const [meta, b64] = dataUrl.split(',');
	const mime  = /data:([^;]+)/.exec(meta)?.[1] ?? 'image/png';
	const bytes = atob(b64);
	const arr   = new Uint8Array(bytes.length);
	for (let i = 0; i < bytes.length; i++) { arr[i] = bytes.charCodeAt(i); }
	return new Blob([arr], { type: mime });
}
